"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import ShowPart from "@/components/content/ShowPart/ShowPart";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const INITIAL_COUNT = 4;

export default function CoursePlanList({ parts }) {
    const t = useTranslations();
    const [isExpanded, setIsExpanded] = useState(false);
    const [allOpen, setAllOpen] = useState(false);
    const [expandTrigger, setExpandTrigger] = useState(0);
    const [collapseTrigger, setCollapseTrigger] = useState(0);

    if (!parts || parts.length === 0) return null;

    const shouldCollapse = parts.length > INITIAL_COUNT;
    const visibleParts =
        shouldCollapse && !isExpanded ? parts.slice(0, INITIAL_COUNT) : parts;
    const remainingCount = parts.length - INITIAL_COUNT;

    let quizCount = 0;
    let challengesCount = 0;
    parts.forEach((part) => {
        if (part.isQuiz) quizCount++;
        else if (part.isChallenge) challengesCount++;
    });

    const handleToggleAll = () => {
        if (allOpen) {
            setCollapseTrigger((v) => v + 1);
        } else {
            setExpandTrigger((v) => v + 1);
        }
        setAllOpen(!allOpen);
    };

    const hasExpandableCourses = parts.some(
        (p) => !p.isQuiz && !p.isChallenge,
    );

    return (
        <>
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-gray-500 dark:text-grayDark-400 font-medium">
                    <span>
                        <span className="font-semibold text-black dark:text-white">
                            {parts.length}
                        </span>{" "}
                        {parts.length > 1
                            ? t("CoursePlanList_partsCount")
                            : t("CoursePlanList_partCount")}
                    </span>
                    {quizCount > 0 && (
                        <>
                            <span
                                className="text-gray-300 dark:text-grayDark-600"
                                aria-hidden
                            >
                                ·
                            </span>
                            <span>
                                <span className="font-semibold text-black dark:text-white">
                                    {quizCount}
                                </span>{" "}
                                {quizCount > 1
                                    ? t("CoursePlanList_quizPlural")
                                    : t("CoursePlanList_quizSingular")}
                            </span>
                        </>
                    )}
                    {challengesCount > 0 && (
                        <>
                            <span
                                className="text-gray-300 dark:text-grayDark-600"
                                aria-hidden
                            >
                                ·
                            </span>
                            <span>
                                <span className="font-semibold text-black dark:text-white">
                                    {challengesCount}
                                </span>{" "}
                                {challengesCount > 1
                                    ? t("CoursePlanList_challengesPlural")
                                    : t("CoursePlanList_challengeSingular")}
                            </span>
                        </>
                    )}
                </div>

                {hasExpandableCourses && (
                    <button
                        onClick={handleToggleAll}
                        className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-600 dark:text-grayDark-400 hover:text-black dark:hover:text-white transition-colors"
                    >
                        <SolarIcon
                            name={
                                allOpen
                                    ? "alt-arrow-up-bold-duotone"
                                    : "alt-arrow-down-bold-duotone"
                            }
                            className="w-4 h-4"
                        />
                        {allOpen
                            ? t("CoursePlanList_collapseAll")
                            : t("CoursePlanList_expandAll")}
                    </button>
                )}
            </div>

            <div className="flex flex-col gap-2">
                {visibleParts.map((part, index) => {
                    let partdur = 0;
                    part.sessions?.forEach((session) => {
                        if (session.duration && session.duration > 0) {
                            partdur += session.duration;
                        }
                    });
                    return (
                        <ShowPart
                            key={`part-${index}`}
                            part={part}
                            partdur={partdur}
                            first={index === 0}
                            expandTrigger={expandTrigger}
                            collapseTrigger={collapseTrigger}
                        />
                    );
                })}
            </div>

            {shouldCollapse && !isExpanded && (
                <button
                    onClick={() => setIsExpanded(true)}
                    className="group mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-200 dark:border-newDark-700 bg-white dark:bg-newDark-800/40 text-[13px] font-semibold text-black dark:text-white hover:border-gray-300 hover:shadow-sm dark:hover:border-bgDark-550 transition-all"
                >
                    {t("CoursePlanList_showMore", {
                        count: remainingCount,
                    })}
                    <SolarIcon
                        name="alt-arrow-down-bold-duotone"
                        className="w-4 h-4 text-gray-400 dark:text-grayDark-500 transition-transform group-hover:translate-y-0.5"
                    />
                </button>
            )}
        </>
    );
}
